import { useEffect, useRef } from "react";

export const useSyncCardHeights = (cardCount: number, isDataLoaded: boolean = true) => {
  // Create stable refs that won't change on re-renders
  const refsArray = useRef(
    Array.from({ length: cardCount }, () => ({
      headerRef: { current: null } as { current: HTMLDivElement | null },
      coreRef: { current: null } as { current: HTMLDivElement | null },
    }))
  ).current;

  useEffect(() => {
    const syncHeights = () => {
      // Don't sync if data isn't loaded yet
      if (!isDataLoaded) {
        return;
      }

      // Check if we're on desktop (lg breakpoint = 1024px)
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

      // Reset all min heights
      refsArray.forEach(({ headerRef, coreRef }) => {
        if (headerRef.current) headerRef.current.style.minHeight = "";
        if (coreRef.current) coreRef.current.style.minHeight = "";
      });

      // Only sync heights on desktop view where cards are side-by-side
      if (!isDesktop) {
        return; // Exit early for mobile/tablet - let cards use natural height
      }

      // Wait for reflow
      requestAnimationFrame(() => {
        // Sync header heights
        const headerHeights = refsArray.map(({ headerRef }) => headerRef.current?.offsetHeight || 0);
        const maxHeaderHeight = Math.max(...headerHeights);
        refsArray.forEach(({ headerRef }) => {
          if (headerRef.current) headerRef.current.style.minHeight = `${maxHeaderHeight}px`;
        });

        // Sync core features heights
        const coreHeights = refsArray.map(({ coreRef }) => coreRef.current?.offsetHeight || 0);
        const maxCoreHeight = Math.max(...coreHeights);
        refsArray.forEach(({ coreRef }) => {
          if (coreRef.current) coreRef.current.style.minHeight = `${maxCoreHeight}px`;
        });
      });
    };

    // Run sync immediately when data is loaded
    if (isDataLoaded) {
      syncHeights();
    }

    const timer = setTimeout(syncHeights, 100);
    window.addEventListener("resize", syncHeights);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", syncHeights);
    };
  }, [refsArray, isDataLoaded]);

  return refsArray;
};
